/**
 * useProjectEntities Hook
 * 
 * Fetches all project entities and provides a serialized context
 * for AI generation with full project awareness.
 */

import { useMemo } from 'react'
import { useCharacters, useLocations, useEvents } from './useFirestore'

/**
 * Hook that fetches all project entities and provides context for AI generation
 * 
 * @param {string|null} projectId - The project ID
 * @returns {object} - { characters, locations, events, loading, getAIContext }
 */
export function useProjectEntities(projectId) {
  const { data: characters, loading: charactersLoading } = useCharacters(projectId)
  const { data: locations, loading: locationsLoading } = useLocations(projectId)
  const { data: events, loading: eventsLoading } = useEvents(projectId)

  const loading = charactersLoading || locationsLoading || eventsLoading

  /**
   * Get a serialized context string for AI generation
   * This provides the AI with full project awareness for better suggestions
   */
  const getAIContext = useMemo(() => {
    return () => {
      if (loading) return null

      const context = {
        characters: (characters || []).map(c => ({
          name: c.name,
          description: c.description?.slice(0, 200),
          attire: c.attire,
          hairStyle: c.hairStyle,
          distinctFeatures: c.distinctFeatures,
        })),
        locations: (locations || []).map(l => ({
          name: l.name,
          description: l.description?.slice(0, 200),
          era: l.era,
          keyElements: l.keyElements,
        })),
        events: (events || []).map(e => ({
          title: e.title,
          description: e.description?.slice(0, 200),
          date: e.date,
          era: e.era,
        })),
        summary: {
          characterCount: (characters || []).length,
          locationCount: (locations || []).length,
          eventCount: (events || []).length,
        },
      }

      return context
    }
  }, [characters, locations, events, loading])

  /**
   * Get a condensed context string for smaller prompts
   */
  const getCondensedContext = useMemo(() => {
    return () => {
      if (loading) return null

      const charNames = (characters || []).map(c => c.name).filter(Boolean).join(', ')
      const locNames = (locations || []).map(l => l.name).filter(Boolean).join(', ')
      const eventTitles = (events || []).map(e => e.title).filter(Boolean).join(', ')

      return {
        characterNames: charNames || 'None yet',
        locationNames: locNames || 'None yet',
        eventTitles: eventTitles || 'None yet',
        summary: `Project has ${(characters || []).length} characters, ${(locations || []).length} locations, ${(events || []).length} events.`,
      }
    }
  }, [characters, locations, events, loading])

  return {
    characters: characters || [],
    locations: locations || [],
    events: events || [],
    loading,
    getAIContext,
    getCondensedContext,
  }
}

export default useProjectEntities
